import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, SimpleGrid, Card, CardHeader, CardBody,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Progress, Badge, Spinner, Alert, AlertIcon,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  Divider, HStack, VStack, Flex, Stat, StatLabel, StatNumber, StatHelpText,
  ButtonGroup, Button, Select,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

// Update this rate when NZD/USD moves significantly.
const USD_TO_NZD = 1.65;

function fmtCost(usd) {
  const nzd = usd * USD_TO_NZD;
  if (nzd < 0.01) return '<NZ$0.01';
  return `NZ$${nzd.toFixed(2)}`;
}

function fmtMs(ms) {
  if (!ms) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function currentMonthKey() {
  return new Date().toISOString().slice(0, 7);
}

function monthLabel(month) {
  if (!month || month === 'all') return 'All time';
  const [year, monthIndex] = month.split('-').map(Number);
  return new Date(year, monthIndex - 1, 1).toLocaleDateString('en-NZ', {
    month: 'long',
    year: 'numeric',
  });
}

function usageQuery(period) {
  return period === 'all' ? 'period=all' : `month=${encodeURIComponent(period)}`;
}

// ─── Overview Tab ────────────────────────────────────────────────────────────
function OverviewTab({ summary, daily, period }) {
  const maxTokens = daily.length ? Math.max(...daily.map(d => d.totalTokens)) : 1;
  const periodLabel = period === 'all' ? 'All Time' : monthLabel(period);
  const trendLabel = period === 'all' ? 'Last 30 Days' : periodLabel;

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing={4} mb={6}>
        <Card variant="outline">
          <CardBody>
            <Stat>
              <StatLabel color="gray.500">Tokens Today</StatLabel>
              <StatNumber fontSize="xl">{fmt(summary?.today?.tokens)}</StatNumber>
              <StatHelpText>{fmt(summary?.today?.calls)} calls</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card variant="outline">
          <CardBody>
            <Stat>
              <StatLabel color="gray.500">Tokens {periodLabel}</StatLabel>
              <StatNumber fontSize="xl">{fmt(summary?.period?.tokens)}</StatNumber>
              <StatHelpText>{fmt(summary?.period?.calls)} calls</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card variant="outline">
          <CardBody>
            <Stat>
              <StatLabel color="gray.500">Est. Cost Today</StatLabel>
              <StatNumber fontSize="xl">{fmtCost(summary?.today?.costUsd || 0)}</StatNumber>
              <StatHelpText>NZD</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card variant="outline">
          <CardBody>
            <Stat>
              <StatLabel color="gray.500">Est. Cost {periodLabel}</StatLabel>
              <StatNumber fontSize="xl">{fmtCost(summary?.period?.costUsd || 0)}</StatNumber>
              <StatHelpText>NZD</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card variant="outline">
          <CardBody>
            <Stat>
              <StatLabel color="gray.500">Total Spent</StatLabel>
              <StatNumber fontSize="xl">{fmtCost(summary?.total?.costUsd || 0)}</StatNumber>
              <StatHelpText>{fmt(summary?.total?.calls)} calls all time</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card variant="outline">
        <CardHeader pb={2}>
          <Heading size="sm">Daily Token Usage — {trendLabel}</Heading>
        </CardHeader>
        <CardBody pt={2}>
          {daily.length === 0 ? (
            <Text color="gray.400" fontSize="sm">No data yet.</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {daily.map(d => (
                <Box key={d.day}>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="xs" color="gray.500">
                      {new Date(d.day).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}
                    </Text>
                    <HStack spacing={3}>
                      <Text fontSize="xs" color="gray.400">{d.calls} calls</Text>
                      <Text fontSize="xs" fontWeight="medium">{fmt(d.totalTokens)} tokens</Text>
                    </HStack>
                  </Flex>
                  <Progress
                    value={(d.totalTokens / maxTokens) * 100}
                    size="sm"
                    colorScheme="blue"
                    borderRadius="full"
                  />
                </Box>
              ))}
            </VStack>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}

// ─── Feature grouping map ─────────────────────────────────────────────────────
// Maps individual feature keys (from ai_token_usage.feature) to a parent label.
// Update this when new logTokenUsage calls are added — no DB changes needed.
const FEATURE_GROUP = {
  // Product Strategy (new names)
  'product-strategy/brief':       'Product Strategy',
  'product-strategy/market-scan': 'Product Strategy',
  'product-strategy/hypothesis':  'Product Strategy',
  'product-strategy/research':    'Product Strategy',
  'product-strategy':             'Product Strategy',
  // First-time plan creation
  'create-plan/brief-analysis':       'Plan Creation',
  'create-plan/brief-deconstruction': 'Plan Creation',
  'create-plan/market-scan-ai':       'Plan Creation',
  'create-plan/design-hypothesis':    'Plan Creation',
  // Product Strategy (legacy names — pre-rename rows in DB)
  'brief-deconstruction':         'Product Strategy',
  'design-hypothesis':            'Product Strategy',
  // Competitor Analysis (new names)
  'competitor-analysis/research':     'Competitor Analysis',
  'competitor-analysis/market':       'Competitor Analysis',
  'competitor-analysis/opportunity':  'Competitor Analysis',
  'competitor-analysis/report':       'Competitor Analysis',
  // Competitor Analysis (legacy names)
  'research-planning':            'Competitor Analysis',
  'competitor-analysis':          'Competitor Analysis',
  'competitor-opportunity':       'Competitor Analysis',
  'competitor-report':            'Competitor Analysis',
  // Action Plan (new names)
  'plan-strategy':                'Action Plan',
  'action-plan/research':         'Action Plan',
  'action-plan/programs':         'Action Plan',
  // Action Plan (legacy names)
  'strategy':                     'Action Plan',
  'program-generation':           'Action Plan',
  // Brief / Onboarding
  'brief-analyze':                'Brief',
  'brief/analyze':                'Brief',
  'generate-goal':                'Brief',
  // Plan Generation
  'generate-content':             'Plan Generation',
  'generate-deliverables':        'Plan Generation',
  'generate-deliverables-rec':    'Plan Generation',
  'generate-stakeholders':        'Plan Generation',
  'generate-total-stakeholders':  'Plan Generation',
  'generate-metrics':             'Plan Generation',
  'generate-principles':          'Plan Generation',
  'generate-sow':                 'Plan Generation',
  'activity-details':             'Plan Generation',
  'activity-info':                'Plan Generation',
  // MCP
  'mcp/ask-pabs-agent':           'MCP',
  // Other
  'chat':                         'Chat',
  'pulse':                        'Pulse',
  'pulse-nudge':                  'Pulse',
  'learnings':                    'Learnings',
  'readme':                       'Readme',
};

function groupFeatureData(rows) {
  const groups = {};
  for (const r of rows) {
    const label = FEATURE_GROUP[r.feature] || r.feature;
    if (!groups[label]) {
      groups[label] = { feature: label, models: new Set(), calls: 0, promptTokens: 0, completionTokens: 0, totalTokens: 0, costUsd: 0 };
    }
    const g = groups[label];
    if (r.model) g.models.add(r.model);
    g.calls            += r.calls;
    g.promptTokens     += r.promptTokens;
    g.completionTokens += r.completionTokens;
    g.totalTokens      += r.totalTokens;
    g.costUsd          += r.costUsd;
  }
  return Object.values(groups)
    .map(g => ({ ...g, models: [...g.models] }))
    .sort((a, b) => b.totalTokens - a.totalTokens);
}

// ─── By Feature Tab ──────────────────────────────────────────────────────────
function ByFeatureTab({ query, scopeLabel }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [grouped, setGrouped] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    fetchWithAuth(`/api/admin/token-usage/by-feature?${query}`)
      .then(r => r.json())
      .then(rows => {
        if (cancelled) return;
        setData(Array.isArray(rows) ? rows : []);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setData([]);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [query]);

  if (!loaded) return <Spinner mt={6} />;
  if (!data || data.length === 0) return <Text color="gray.400" mt={4}>No feature data yet.</Text>;

  const rows = grouped ? groupFeatureData(data) : data;
  const maxTokens = Math.max(...rows.map(r => r.totalTokens));

  return (
    <Card variant="outline">
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <Heading size="sm">Token Usage by Feature — {scopeLabel}</Heading>
          <ButtonGroup size="xs" isAttached>
            <Button onClick={() => setGrouped(true)}  variant={grouped ? 'solid' : 'outline'}  colorScheme="blue">Grouped</Button>
            <Button onClick={() => setGrouped(false)} variant={!grouped ? 'solid' : 'outline'} colorScheme="blue">Detailed</Button>
          </ButtonGroup>
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        <TableContainer>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Feature</Th>
                <Th>Model</Th>
                <Th isNumeric>Calls</Th>
                <Th isNumeric>Prompt</Th>
                <Th isNumeric>Completion</Th>
                <Th isNumeric>Total</Th>
                <Th isNumeric>Est. Cost</Th>
                {!grouped && <Th isNumeric>Avg Latency</Th>}
                <Th w="120px">Volume</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r, i) => (
                <Tr key={i}>
                  <Td fontWeight="medium">{r.feature}</Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      {grouped
                        ? r.models.map(m => <Badge key={m} colorScheme="purple" fontSize="xs">{m}</Badge>)
                        : <Badge colorScheme="purple" fontSize="xs">{r.model}</Badge>
                      }
                    </HStack>
                  </Td>
                  <Td isNumeric>{fmt(r.calls)}</Td>
                  <Td isNumeric color="gray.500">{fmt(r.promptTokens)}</Td>
                  <Td isNumeric color="gray.500">{fmt(r.completionTokens)}</Td>
                  <Td isNumeric fontWeight="medium">{fmt(r.totalTokens)}</Td>
                  <Td isNumeric color="green.600">{fmtCost(r.costUsd)}</Td>
                  {!grouped && <Td isNumeric color="gray.500">{fmtMs(r.avgDurationMs)}</Td>}
                  <Td>
                    <Progress
                      value={(r.totalTokens / maxTokens) * 100}
                      size="sm"
                      colorScheme="blue"
                      borderRadius="full"
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
}

// ─── By Model Tab ────────────────────────────────────────────────────────────
function ByModelTab({ query, scopeLabel }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    fetchWithAuth(`/api/admin/token-usage/by-model?${query}`)
      .then(r => r.json())
      .then(rows => {
        if (cancelled) return;
        setData(Array.isArray(rows) ? rows : []);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setData([]);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [query]);

  if (!loaded) return <Spinner mt={6} />;
  if (!data || data.length === 0) return <Text color="gray.400" mt={4}>No model data yet.</Text>;

  const maxCost = Math.max(...data.map(r => r.costUsd));

  return (
    <Card variant="outline">
      <CardHeader pb={2}>
        <Heading size="sm">Token Usage by Model — {scopeLabel}</Heading>
      </CardHeader>
      <CardBody pt={0}>
        <TableContainer>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Model</Th>
                <Th isNumeric>Calls</Th>
                <Th isNumeric>Prompt Tokens</Th>
                <Th isNumeric>Completion Tokens</Th>
                <Th isNumeric>Total Tokens</Th>
                <Th isNumeric>Est. Cost (NZD)</Th>
                <Th w="120px">Cost Share</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((r, i) => (
                <Tr key={i}>
                  <Td fontWeight="medium">
                    <Badge colorScheme="purple" fontSize="xs">{r.model}</Badge>
                  </Td>
                  <Td isNumeric>{fmt(r.calls)}</Td>
                  <Td isNumeric color="gray.500">{fmt(r.promptTokens)}</Td>
                  <Td isNumeric color="gray.500">{fmt(r.completionTokens)}</Td>
                  <Td isNumeric fontWeight="medium">{fmt(r.totalTokens)}</Td>
                  <Td isNumeric fontWeight="semibold" color="green.600">{fmtCost(r.costUsd)}</Td>
                  <Td>
                    <Progress
                      value={maxCost > 0 ? (r.costUsd / maxCost) * 100 : 0}
                      size="sm"
                      colorScheme="green"
                      borderRadius="full"
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
}

// ─── By User Tab ─────────────────────────────────────────────────────────────
function ByUserTab({ query, scopeLabel }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [featureGrouped, setFeatureGrouped] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setSelectedUser(null);
    fetchWithAuth(`/api/admin/token-usage/by-user?${query}`)
      .then(r => r.json())
      .then(rows => {
        if (cancelled) return;
        setData(Array.isArray(rows) ? rows : []);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setData([]);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [query]);

  const openUser = useCallback(async (user) => {
    setSelectedUser(user);
    setUserDetail(null);
    setDrawerLoading(true);
    try {
      const res = await fetchWithAuth(`/api/admin/token-usage/user/${user.userId}?${query}`);
      const detail = await res.json();
      setUserDetail(detail);
    } catch (e) {
      setUserDetail(null);
    }
    setDrawerLoading(false);
  }, [query]);

  if (!loaded) return <Spinner mt={6} />;
  if (!data || data.length === 0) return <Text color="gray.400" mt={4}>No user data for {scopeLabel}.</Text>;

  const maxTokens = Math.max(...data.map(r => r.totalTokens));

  return (
    <>
      <Card variant="outline">
        <CardHeader pb={2}>
          <Heading size="sm">Top Users — {scopeLabel}</Heading>
        </CardHeader>
        <CardBody pt={0}>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>User</Th>
                  <Th isNumeric>Calls</Th>
                  <Th isNumeric>Total Tokens</Th>
                  <Th isNumeric>Est. Cost</Th>
                  <Th w="120px">Volume</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map((r, i) => (
                  <Tr
                    key={i}
                    onClick={() => openUser(r)}
                    cursor="pointer"
                    _hover={{ bg: 'gray.50' }}
                  >
                    <Td fontWeight="medium" color="blue.600">{r.email}</Td>
                    <Td isNumeric>{fmt(r.calls)}</Td>
                    <Td isNumeric fontWeight="medium">{fmt(r.totalTokens)}</Td>
                    <Td isNumeric color="green.600">{fmtCost(r.costUsd)}</Td>
                    <Td>
                      <Progress
                        value={(r.totalTokens / maxTokens) * 100}
                        size="sm"
                        colorScheme="blue"
                        borderRadius="full"
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      <Drawer isOpen={!!selectedUser} placement="right" onClose={() => setSelectedUser(null)} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text fontSize="md" fontWeight="bold">{selectedUser?.email}</Text>
            <Text fontSize="xs" color="gray.500" fontWeight="normal">Usage breakdown for {scopeLabel}</Text>
          </DrawerHeader>
          <DrawerBody py={4}>
            {drawerLoading && <Spinner />}
            {!drawerLoading && userDetail && (
              <VStack align="stretch" spacing={5}>
                {/* Per-plan breakdown */}
                <Box>
                  <Heading size="xs" mb={3} color="gray.600">BY PLAN</Heading>
                  {userDetail.plans.length === 0 ? (
                    <Text fontSize="sm" color="gray.400">No plan-linked usage.</Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {userDetail.plans.map(p => (
                        <Box key={p.planId} p={3} border="1px" borderColor="gray.200" borderRadius="md">
                          <Flex justify="space-between">
                            <Box>
                              <Text fontWeight="medium" fontSize="sm">{p.planName}</Text>
                              {p.workflowId && (
                                <Text fontSize="xs" color="gray.400">{p.workflowId}</Text>
                              )}
                            </Box>
                            <Box textAlign="right">
                              <Text fontSize="sm" fontWeight="semibold">{fmt(p.totalTokens)} tokens</Text>
                              <Text fontSize="xs" color="green.600">{fmtCost(p.costUsd)}</Text>
                            </Box>
                          </Flex>
                          <Text fontSize="xs" color="gray.400" mt={1}>{p.calls} calls</Text>
                        </Box>
                      ))}
                      {userDetail.unlinked.totalTokens > 0 && (
                        <Box p={3} border="1px" borderColor="gray.100" borderRadius="md" bg="gray.50">
                          <Flex justify="space-between">
                            <Text fontSize="sm" color="gray.500">Unlinked (no plan)</Text>
                            <Box textAlign="right">
                              <Text fontSize="sm">{fmt(userDetail.unlinked.totalTokens)} tokens</Text>
                              <Text fontSize="xs" color="green.600">{fmtCost(userDetail.unlinked.costUsd)}</Text>
                            </Box>
                          </Flex>
                          <Text fontSize="xs" color="gray.400" mt={1}>{userDetail.unlinked.calls} calls</Text>
                        </Box>
                      )}
                    </VStack>
                  )}
                </Box>

                <Divider />

                {/* Per-feature breakdown */}
                <Box>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Heading size="xs" color="gray.600">BY FEATURE</Heading>
                    <ButtonGroup size="xs" isAttached>
                      <Button onClick={() => setFeatureGrouped(true)}  variant={featureGrouped ? 'solid' : 'outline'}  colorScheme="blue">Grouped</Button>
                      <Button onClick={() => setFeatureGrouped(false)} variant={!featureGrouped ? 'solid' : 'outline'} colorScheme="blue">Detailed</Button>
                    </ButtonGroup>
                  </Flex>
                  {userDetail.features.length === 0 ? (
                    <Text fontSize="sm" color="gray.400">No feature data.</Text>
                  ) : (() => {
                    const rows = featureGrouped ? groupFeatureData(userDetail.features) : userDetail.features;
                    return (
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>Feature</Th>
                            <Th>Model</Th>
                            <Th isNumeric>Calls</Th>
                            <Th isNumeric>Tokens</Th>
                            <Th isNumeric>Cost</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {rows.map((f, i) => (
                            <Tr key={i}>
                              <Td fontSize="xs" fontWeight="medium">{f.feature}</Td>
                              <Td>
                                <HStack spacing={1} flexWrap="wrap">
                                  {featureGrouped
                                    ? f.models.map(m => <Badge key={m} colorScheme="purple" fontSize="2xs">{m}</Badge>)
                                    : <Badge colorScheme="purple" fontSize="2xs">{f.model}</Badge>
                                  }
                                </HStack>
                              </Td>
                              <Td isNumeric fontSize="xs">{f.calls}</Td>
                              <Td isNumeric fontSize="xs">{fmt(f.totalTokens)}</Td>
                              <Td isNumeric fontSize="xs" color="green.600">{fmtCost(f.costUsd)}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    );
                  })()}
                </Box>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

// ─── By Plan Tab ─────────────────────────────────────────────────────────────
function ByPlanTab({ query, scopeLabel }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planDetail, setPlanDetail] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [featureGrouped, setFeatureGrouped] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setSelectedPlan(null);
    fetchWithAuth(`/api/admin/token-usage/by-plan?${query}`)
      .then(r => r.json())
      .then(rows => {
        if (cancelled) return;
        setData(Array.isArray(rows) ? rows : []);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setData([]);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [query]);

  const openPlan = useCallback(async (plan) => {
    setSelectedPlan(plan);
    setPlanDetail(null);
    setDrawerLoading(true);
    try {
      const res = await fetchWithAuth(`/api/admin/token-usage/plan/${plan.planId}?${query}`);
      const detail = await res.json();
      setPlanDetail(detail);
    } catch (e) {
      setPlanDetail(null);
    }
    setDrawerLoading(false);
  }, [query]);

  if (!loaded) return <Spinner mt={6} />;
  if (!data || data.length === 0) return <Text color="gray.400" mt={4}>No plan-linked usage yet. Plans appear here when AI features are used within them.</Text>;

  const maxTokens = Math.max(...data.map(r => r.totalTokens));

  return (
    <>
      <Card variant="outline">
        <CardHeader pb={2}>
          <Heading size="sm">AI Usage by Plan — {scopeLabel}</Heading>
        </CardHeader>
        <CardBody pt={0}>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Plan</Th>
                  <Th>Workflow</Th>
                  <Th>User</Th>
                  <Th isNumeric>Calls</Th>
                  <Th isNumeric>Total Tokens</Th>
                  <Th isNumeric>Est. Cost</Th>
                  <Th>Last Activity</Th>
                  <Th w="100px">Volume</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map((r, i) => (
                  <Tr
                    key={i}
                    onClick={() => openPlan(r)}
                    cursor="pointer"
                    _hover={{ bg: 'gray.50' }}
                  >
                    <Td fontWeight="medium" color="blue.600" maxW="180px">
                      <Text noOfLines={1}>{r.planName}</Text>
                    </Td>
                    <Td>
                      {r.workflowId && (
                        <Badge colorScheme="blue" fontSize="xs">{r.workflowId}</Badge>
                      )}
                    </Td>
                    <Td fontSize="xs" color="gray.500">{r.userEmail || '—'}</Td>
                    <Td isNumeric>{fmt(r.calls)}</Td>
                    <Td isNumeric fontWeight="medium">{fmt(r.totalTokens)}</Td>
                    <Td isNumeric color="green.600">{fmtCost(r.costUsd)}</Td>
                    <Td fontSize="xs" color="gray.400">
                      {r.lastActivity ? new Date(r.lastActivity).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' }) : '—'}
                    </Td>
                    <Td>
                      <Progress
                        value={(r.totalTokens / maxTokens) * 100}
                        size="sm"
                        colorScheme="teal"
                        borderRadius="full"
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      <Drawer isOpen={!!selectedPlan} placement="right" onClose={() => setSelectedPlan(null)} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text fontSize="md" fontWeight="bold" noOfLines={1}>{selectedPlan?.planName}</Text>
            <HStack spacing={2} mt={1}>
              {selectedPlan?.workflowId && (
                <Badge colorScheme="blue" fontSize="xs">{selectedPlan.workflowId}</Badge>
              )}
              <Text fontSize="xs" color="gray.500">{selectedPlan?.userEmail}</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody py={4}>
            {drawerLoading && <Spinner />}
            {!drawerLoading && planDetail && (
              <Box>
                <Flex justify="space-between" align="center" mb={3}>
                  <Heading size="xs" color="gray.600">FEATURE BREAKDOWN</Heading>
                  <ButtonGroup size="xs" isAttached>
                    <Button onClick={() => setFeatureGrouped(true)}  variant={featureGrouped ? 'solid' : 'outline'}  colorScheme="blue">Grouped</Button>
                    <Button onClick={() => setFeatureGrouped(false)} variant={!featureGrouped ? 'solid' : 'outline'} colorScheme="blue">Detailed</Button>
                  </ButtonGroup>
                </Flex>
                {planDetail.length === 0 ? (
                  <Text fontSize="sm" color="gray.400">No feature data for this plan.</Text>
                ) : (() => {
                  const rows = featureGrouped ? groupFeatureData(planDetail) : planDetail;
                  return (
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Feature</Th>
                          <Th>Model</Th>
                          <Th isNumeric>Calls</Th>
                          <Th isNumeric>Tokens</Th>
                          <Th isNumeric>Cost</Th>
                          {!featureGrouped && <Th isNumeric>Latency</Th>}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {rows.map((f, i) => (
                          <Tr key={i}>
                            <Td fontSize="xs" fontWeight="medium" maxW="140px">
                              <Text noOfLines={2} whiteSpace="normal">{f.feature}</Text>
                            </Td>
                            <Td>
                              <HStack spacing={1} flexWrap="wrap">
                                {featureGrouped
                                  ? f.models.map(m => <Badge key={m} colorScheme="purple" fontSize="2xs">{m}</Badge>)
                                  : <Badge colorScheme="purple" fontSize="2xs">{f.model}</Badge>
                                }
                              </HStack>
                            </Td>
                            <Td isNumeric fontSize="xs">{f.calls}</Td>
                            <Td isNumeric fontSize="xs">{fmt(f.totalTokens)}</Td>
                            <Td isNumeric fontSize="xs" color="green.600">{fmtCost(f.costUsd)}</Td>
                            {!featureGrouped && <Td isNumeric fontSize="xs" color="gray.400">{fmtMs(f.avgDurationMs)}</Td>}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  );
                })()}
              </Box>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function AdminTokenUsage() {
  const [tabIndex, setTabIndex] = useState(0);
  const [months, setMonths] = useState([]);
  const [period, setPeriod] = useState(currentMonthKey());
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const query = usageQuery(period);
  const scopeLabel = period === 'all' ? 'All Time' : monthLabel(period);

  useEffect(() => {
    let cancelled = false;
    fetchWithAuth('/api/admin/token-usage/months')
      .then(r => r.json())
      .then(rows => {
        if (cancelled) return;
        const monthRows = Array.isArray(rows) ? rows : [];
        const current = currentMonthKey();
        const hasCurrent = monthRows.some(row => row.month === current);
        setMonths(hasCurrent ? monthRows : [{ month: current, calls: 0, totalTokens: 0 }, ...monthRows]);
      })
      .catch(() => {
        if (!cancelled) setMonths([{ month: currentMonthKey(), calls: 0, totalTokens: 0 }]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchWithAuth(`/api/admin/token-usage/summary?${query}`).then(r => r.json()),
      fetchWithAuth(`/api/admin/token-usage/daily?${query}`).then(r => r.json()),
    ])
      .then(([s, d]) => {
        if (cancelled) return;
        setSummary(s);
        setDaily(Array.isArray(d) ? d : []);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [query]);

  if (loading) {
    return (
      <Box p={6} display="flex" justifyContent="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Failed to load token usage data: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex mb={6} justify="space-between" align={{ base: 'stretch', md: 'flex-end' }} gap={4} direction={{ base: 'column', md: 'row' }}>
        <Box>
          <Heading size="md" mb={1}>Token Usage</Heading>
          <Text color="gray.500" fontSize="sm">AI API usage and estimated costs across all features and users.</Text>
        </Box>
        <Select
          size="sm"
          maxW={{ base: '100%', md: '220px' }}
          value={period}
          onChange={e => setPeriod(e.target.value)}
        >
          <option value="all">All time</option>
          {months.map(row => (
            <option key={row.month} value={row.month}>
              {monthLabel(row.month)}
            </option>
          ))}
        </Select>
      </Flex>

      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="blue" variant="enclosed" isLazy>
        <TabList>
          <Tab>Overview</Tab>
          <Tab>By Feature</Tab>
          <Tab>By Model</Tab>
          <Tab>By User</Tab>
          <Tab>By Plan</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <OverviewTab summary={summary} daily={daily} period={period} />
          </TabPanel>
          <TabPanel px={0}>
            <ByFeatureTab query={query} scopeLabel={scopeLabel} />
          </TabPanel>
          <TabPanel px={0}>
            <ByModelTab query={query} scopeLabel={scopeLabel} />
          </TabPanel>
          <TabPanel px={0}>
            <ByUserTab query={query} scopeLabel={scopeLabel} />
          </TabPanel>
          <TabPanel px={0}>
            <ByPlanTab query={query} scopeLabel={scopeLabel} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
